import ArrowRight from "@assets/icons/arrow-right.svg";
import Settings from "@assets/icons/settings.svg";
import { Button, MiniButton } from "@components/button";
import { EmptyState } from "@components/empty-state";
import { FilterModal, FilterProps } from "@components/filter-modal";
import { Input } from "@components/input";
import { FlexLoader } from "@components/loader";
import { useBoolState } from "@hooks/use-bool-state";
import { usePagination } from "@hooks/use-pagination";
import classNames from "classnames";
import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import styles from "./table.module.scss";
import SortingComponent, { SortingConfig } from "./sorting";
import { debounce } from "@helpers/debounce";

export type Config<T extends Record<string, unknown>> = {
  [K in keyof T]: {
    source: K;
    label: string;
    visible?: boolean | ((cellData: T[K], item: T) => boolean);
    renderComponent?: (cellData: T[K], item: T) => ReactNode;
  };
}[keyof T][];

type PaginationType = ReturnType<typeof usePagination> & {
  totalCount: number;
  limits: number[];
};

type Props<
  T extends Record<string, unknown>,
  F extends Record<string, unknown>,
> = {
  data?: T[] | null;
  config: Config<T>;
  className?: string;
  cellClassName?: string;
  withFilter?: boolean;
  title?: string;
  placeholder?: string;
  onRowClick?: (item: T, rowIndex: number) => void;
  loading?: boolean;
  emptyText?: string;
  filterProps?: FilterProps<F>;
  sortingConfig?: SortingConfig;
  onSearch?: (query: string) => void;
} & Partial<PaginationType>;

export const Table = <
  T extends Record<string, unknown>,
  F extends Record<string, unknown>,
>({
  data,
  config,
  className,
  cellClassName,
  withFilter,
  title,
  placeholder,
  loading,
  emptyText = "Список пуст",
  onRowClick,
  setLimit,
  setPage,
  limit,
  page,
  totalCount,
  filterProps,
  sortingConfig,
  limits,
  onSearch
}: Props<T, F>) => {
  const { control, watch } = useForm();
  const [isModalOpen, showModal, hideModal] = useBoolState(false);
  // Watch the search input value
  const searchValue = watch("search");

  // Debounced function to send data to the backend
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query != null && onSearch) {
        onSearch(query);
      }
    }, 500), // 500ms debounce delay
    []
  );
  // Trigger the debounced search when searchValue changes
  useEffect(() => {
    debouncedSearch(searchValue);
  }, [searchValue, debouncedSearch]);
  return (
    <>
      {title && <h3 className={styles.title}>{title}</h3>}
      {withFilter && (
        <div className={styles.header}>
          <div className={styles.search}>
            <Input
              name="search"
              control={control}
              type="text"
              placeholder={placeholder}
              disabled={onSearch == null}
            />
          </div>
          <Button
            className={styles.filterButton}
            type="outline"
            icon={Settings}
            onClick={showModal}
          >
            Параметры поиска
          </Button>
          {sortingConfig && <SortingComponent config={sortingConfig} />}
          {isModalOpen && filterProps && (
            <FilterModal
              isModalOpen={isModalOpen}
              hideModal={hideModal}
              {...filterProps}
            />
          )}
        </div>
      )}
      {loading ? (
        <FlexLoader />
      ) : !data?.length ? (
        <EmptyState text={emptyText} className={styles.empty} />
      ) : (
        <>
          <table className={classNames(styles.table, className)}>
            <thead>
              <tr>
                {config.map((column, index) => (
                  <th key={index} className={styles.tableHeader}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={classNames(
                    styles.tableRow,
                    onRowClick && styles.tableRowClickable
                  )}
                  onClick={() => onRowClick?.(item, rowIndex)}
                >
                  {config.map((column, colIndex) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const cellData = item[column.source] as any;
                    return (
                      <td
                        key={colIndex}
                        className={classNames(styles.tableCell, cellClassName)}
                      >
                        {column.renderComponent?.(cellData, item) ?? cellData}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {setLimit && setPage && !!limit && !!page && !!totalCount && (
            <Pagination
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              totalCount={totalCount}
              limits={limits ?? [10, 20, 30]}
            />
          )}
        </>
      )}
    </>
  );
};

const Pagination = ({
  limit,
  setLimit,
  page,
  setPage,
  totalCount,
  limits,
}: PaginationType) => {
  const totalPages = Math.ceil(totalCount / limit);
  const isLastPage = page >= totalPages;

  const pages = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 3) {
      return [1, 2, 3, "...", totalPages];
    }
    if (page >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "...", page - 1, page, page + 1, "...", totalPages];
  }, [page, totalPages]);

  return (
    <div className={styles.pagination}>
      <div className={styles.limit}>
        {limits.map((size) => (
          <MiniButton
            key={size}
            active={size === limit}
            className={styles.button}
            onClick={() => setLimit(size)}
          >
            {size}
          </MiniButton>
        ))}
      </div>
      <div className={styles.page}>
        <MiniButton
          className={classNames(styles.button, styles.arrow, styles.leftArrow)}
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          <ArrowRight />
        </MiniButton>
        {pages.map((num, index) =>
          typeof num === "number" ? (
            <MiniButton
              key={num}
              active={num === page}
              className={styles.button}
              onClick={() => setPage(num)}
            >
              {num}
            </MiniButton>
          ) : (
            <span key={`dots-${index}`} className={styles.dots}>
              {num}
            </span>
          )
        )}
        <MiniButton
          className={classNames(styles.button, styles.arrow)}
          disabled={isLastPage}
          onClick={() => setPage(page + 1)}
        >
          <ArrowRight />
        </MiniButton>
      </div>
    </div>
  );
};

type CustomCellProps = {
  text: string | number;
  active: boolean;
};

export const CustomCell = ({ text, active }: CustomCellProps) => {
  return (
    <span
      className={classNames(
        styles.customCell,
        active ? styles.active : styles.notActive
      )}
    >
      {text}
    </span>
  );
};

type CustomCellWithIconProps = CustomCellProps & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
};

export const CustomCellWithIcon = ({
  text,
  active,
  icon: Icon,
}: CustomCellWithIconProps) => {
  return (
    <div className={styles.customCellWithIcon}>
      <CustomCell text={text} active={active} />
      <div className={styles.icon}>
        <Icon />
      </div>
    </div>
  );
};

type DoubleTextProps = {
  text: string | number;
  subText: string | number;
};

export const DoubleText = ({ text, subText }: DoubleTextProps) => {
  return (
    <>
      {text}
      <p className={styles.subText}>{subText}</p>
    </>
  );
};
